import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api, { tokenStore, setUnauthorizedHandler, apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => tokenStore.getUser());
  const [loading, setLoading] = useState(true);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore network errors on logout */
    }
    tokenStore.clear();
    setUser(null);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
    });
  }, []);

  useEffect(() => {
    const token = tokenStore.get();
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => {
        setUser(res.data);
        tokenStore.set({ user: res.data });
      })
      .catch(() => {
        tokenStore.clear();
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const res = await api.post("/auth/login", { email, password });
      const { accessToken, refreshToken, user: u } = res.data;
      tokenStore.set({ accessToken, refreshToken, user: u });
      setUser(u);
      return { ok: true };
    } catch (error) {
      const status = error?.response?.status;
      const msg =
        status === 401 || status === 400
          ? "Invalid email or password."
          : apiErrorMessage(error, "Login failed.");
      return { ok: false, message: msg };
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, role: user?.role }),
    [user, loading, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
